import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Button from 'components/button';
import {
  INVOICE_REMINDER_CHANGED,
  formatFullDay,
  invoiceRemindersText,
  litigationErrorText,
} from 'components/invoiceReminders/invoiceRemindersText';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

const MODAL_TEXTS = { start: 'startModal', end: 'endModal' };

// Refusals saying the invoice moved elsewhere (another admin, a payment): the
// row behind is stale, so the tab and the detail read it again.
const STATE_MOVED_REFUSALS = [
  'ALREADY_IN_LITIGATION',
  'NOT_IN_LITIGATION',
  'INVOICE_SETTLED',
  'INVOICE_NOT_FOUND',
];

/**
 * Confirms a move to litigation or out of it (JBWEB-1284). `modalData` is
 * `{ number, action }`, `action` being `start` (from "À faire" or the
 * invoice's detail) or `end` (from the "Contentieux" tab).
 *
 * A success fires INVOICE_REMINDER_CHANGED: the tab, its cards, the page's
 * counter and the detail's history read themselves again, as after a manual
 * reminder. A refusal stays in the modal with the text of its code. When it
 * says the invoice moved elsewhere, or when no answer came back (the change
 * may have been saved), the same event reads the screen behind again; a new
 * try stays possible, the api answers a readable 409 if the first went through.
 */
const ModalInvoiceLitigation = () => {
  const { toggle } = useModal();
  const { language, modalData, setModalDataReturn } = UseContext();
  const text = invoiceRemindersText(language);
  const [isPending, setIsPending] = useState(false);
  const [failure, setFailure] = useState(null);
  // State lands one render late: a double click must still send one request.
  const isSending = useRef(false);

  const number = modalData?.number;
  const action = modalData?.action;
  const textsKey = MODAL_TEXTS[action];
  if (!number || !textsKey) return null;
  const modalText = (key, values) =>
    text(`litigation.${textsKey}.${key}`, values);

  const onSuccess = (data) => {
    isSending.current = false;
    setIsPending(false);
    if (action === 'start') {
      toast.success(modalText('done', { number }));
    } else {
      const nextDay = formatFullDay(data?.nextAutomaticAt);
      toast.success(
        nextDay
          ? modalText('doneNext', { number, date: nextDay })
          : modalText('doneNone', { number }),
      );
    }
    setModalDataReturn({ type: INVOICE_REMINDER_CHANGED, number });
    toggle();
  };

  const onFailure = (error) => {
    if (error?.name === 'CanceledError') return;
    isSending.current = false;
    setIsPending(false);
    // Never the error object in the console: axios keeps the request, token included.
    setFailure(litigationErrorText(text, error));
    const code = error?.response?.data?.errorCode;
    if (!error?.response || STATE_MOVED_REFUSALS.includes(code)) {
      setModalDataReturn({ type: INVOICE_REMINDER_CHANGED, number });
    }
  };

  const confirm = () => {
    if (isSending.current) return;
    isSending.current = true;
    setIsPending(true);
    setFailure(null);
    const call =
      action === 'start'
        ? ApiDatabase.postInvoiceLitigation
        : ApiDatabase.deleteInvoiceLitigation;
    call({ number }, onSuccess, onFailure);
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={!isPending}>
        {modalText('title')}
      </Modalheader>
      <ModalBody>
        <div className="flex flex-col gap-3 text-sm">
          <p className="text-gray-700">{modalText('text', { number })}</p>
          {failure && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 px-4 py-3 text-red-700"
            >
              {failure}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          size="LG"
          color={isPending ? 'DISABLED' : 'SECONDARY'}
          disabled={isPending}
          onClick={toggle}
        >
          {modalText('cancel')}
        </Button>
        <Button
          size="LG"
          color={isPending ? 'DISABLED' : 'PRIMARY'}
          disabled={isPending}
          onClick={confirm}
        >
          {isPending ? modalText('pending') : modalText('confirm')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalInvoiceLitigation;
